import React, {useState, useEffect, useMemo, useRef} from 'react';
import {useParams, useNavigate, Link as RouterLink} from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Grid,
    Button,
    Alert,
    TextField,
    FormControlLabel,
    Switch,
    Snackbar,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    Link,
    DialogContent, Avatar, FormHelperText, DialogActions, IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import apiService from '../../services/apiService';
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {MaterialReactTable, useMaterialReactTable} from "material-react-table";
import AddIcon from "@mui/icons-material/Add";
import ImageUploader from "../../components/ImageUploader";
import {CloudUploadIcon} from "lucide-react";
import {ROLES} from "../../config/roles";
import {useAuthorization} from "../../hooks/useAuthorization";
import Protected from "../../components/Protected";
import {ProtectedFieldSet} from "../../context/AuthDisabledContext";

// --- Price Management Modal ---
const EditPriceModal = ({open, onClose, onSave, allRewards, existingPrices, initialData}) => {
    const isEditing = !!initialData;
    const [selectedRewardId, setSelectedRewardId] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isEditing) {
            setSelectedRewardId(initialData.rewardId);
            setQuantity(initialData.quantity);
        }
    }, [initialData, isEditing]);

    const handleSave = () => {
        if (validate()) {
            onSave({rewardId: selectedRewardId, quantity});
            handleClose();
        }
    };

    const handleClose = () => {
        setSelectedRewardId('');
        setQuantity(1);
        setErrors({});
        onClose();
    };

    const validate = () => {
        const newErrors = {};
        if (!selectedRewardId) newErrors.reward = 'A reward must be selected.';
        if (!quantity || quantity <= 0) newErrors.amount = 'Quantity must be greater than 0.';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const availableRewards = allRewards.filter(reward =>
        !existingPrices.some(p => p.rewardId === reward.id) || (isEditing && selectedRewardId === reward.id)
    );

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
            <DialogTitle>{isEditing ? 'Edit Price' : 'Add New Price'}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{mt: 1}}>
                    <FormControl fullWidth error={!!errors.reward}>
                        <InputLabel>Reward</InputLabel>
                        <Select
                            value={selectedRewardId}
                            label="Reward"
                            onChange={e => setSelectedRewardId(e.target.value)}
                            disabled={isEditing}
                        >
                            {availableRewards.map(reward => (
                                <MenuItem key={reward.id} value={reward.id}>
                                    <Stack direction="row" alignItems="center" spacing={1.5}>
                                        <Avatar src={reward.iconUrl} sx={{width: 24, height: 24}}/>
                                        <Typography>{reward.name}</Typography>
                                    </Stack>
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.reward && <FormHelperText>{errors.reward}</FormHelperText>}
                    </FormControl>
                    <TextField
                        autoFocus
                        label="Quantity"
                        type="number"
                        fullWidth
                        value={quantity}
                        onChange={e => setQuantity(parseInt(e.target.value, 10))}
                        error={!!errors.amount}
                        helperText={errors.amount || ''}
                        slotProps={{htmlInput: {min: 1}}}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained">Save</Button>
            </DialogActions>
        </Dialog>
    );
};

// --- Price Management Table ---
const PrizePricesManager = ({prices, setPrices, allRewards}) => {
    const [isModalOpen, setModalOpen] = useState(false);
    const [editingPrice, setEditingPrice] = useState(null);

    const {can} = useAuthorization(PrizeItemEditPage);

    const handleSavePrice = (newPrice) => {
        let updatedPrices;
        if (editingPrice) {
            updatedPrices = prices.map(p =>
                p.rewardId === newPrice.rewardId
                    ? {...p, quantity: newPrice.quantity} :
                    p);
        } else {
            const rewardDetails = allRewards.find(r => r.id === newPrice.rewardId);
            updatedPrices = [...prices, {
                ...newPrice,
                rewardName: rewardDetails.name,
                iconUrl: rewardDetails.iconUrl
            }];
        }
        setPrices(updatedPrices);
        setEditingPrice(null);
    };

    const handleRemovePrice = (rewardId) => {
        setPrices(prices.filter(p => p.rewardId !== rewardId));
    };

    const columns = useMemo(() => [
        {
            accessorKey: 'rewardName',
            header: 'Reward',
            Cell: ({cell, row}) => {
                const {rewardId, iconUrl} = row.original;
                return (
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar src={iconUrl} sx={{width: 32, height: 32}}/>
                        <Link component={RouterLink} to={`/rewards/${rewardId}`}>
                            {cell.getValue()}
                        </Link>
                    </Stack>
                );
            }
        },
        {
            accessorKey: 'quantity',
            header: 'Quantity',
            size: 100
        },
    ], []);

    const table = useMaterialReactTable({
        columns,
        data: prices,
        enableToolbarInternalActions: false,
        enablePagination: false,
        enableBottomToolbar: false,
        enableTopToolbar: can('managePrices'),
        renderTopToolbarCustomActions: () => (
            <Protected component={PrizeItemEditPage} action="managePrices">
                <Button
                    variant="text"
                    startIcon={<AddIcon/>}
                    onClick={() => {
                        setEditingPrice(null);
                        setModalOpen(true);
                    }}
                >
                    Add Price
                </Button>
            </Protected>
        ),
        enableRowActions: can('managePrices'),
        renderRowActions: ({row}) => (
            <Stack direction="row">
                <IconButton onClick={() => {
                    setEditingPrice(row.original);
                    setModalOpen(true);
                }}><EditIcon/></IconButton>
                <IconButton color="error"
                            onClick={() => handleRemovePrice(row.original.rewardId)}><DeleteIcon/></IconButton>
            </Stack>
        ),
    });

    return (
        <Paper sx={{p: 2}}>
            <Typography variant="h6" gutterBottom>Prices</Typography>
            <MaterialReactTable table={table}/>
            <EditPriceModal
                open={isModalOpen}
                onClose={() => setModalOpen(false)}
                onSave={handleSavePrice}
                allRewards={allRewards}
                existingPrices={prices}
                initialData={editingPrice}
            />
        </Paper>
    );
};

// --- Main Prize Item Edit Page ---
const PrizeItemEditPage = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const isNew = id === 'new';
    const requiredAction = isNew ? 'create' : 'update';

    // Form state
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [sortOrder, setSortOrder] = useState(0);
    const [stock, setStock] = useState(0);
    const [managesStock, setManagesStock] = useState(true);
    const [active, setActive] = useState(true);
    const [categoryId, setCategoryId] = useState('');
    const [prices, setPrices] = useState([]);
    const [allRewards, setAllRewards] = useState([]);

    // Data for the category dropdown
    const [categories, setCategories] = useState([]);

    // Page state
    const [loading, setLoading] = useState(!isNew);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({open: false, message: ''});

    // Fetch necessary data on component mount
    useEffect(() => {
        setLoading(true);
        Promise.all([
            apiService.get('/api/admin/prizes/categories'),
            apiService.get('/api/rewards'), // Fetch master list of rewards
            isNew ? Promise.resolve(null) : apiService.get(`/api/admin/prizes/items/${id}`)
        ])
            .then(([categoriesData, rewardsData, itemData]) => {
                setCategories(categoriesData);
                setAllRewards(rewardsData);
                if (itemData) {
                    setName(itemData.name);
                    setDescription(itemData.description);
                    setImageUrl(itemData.imageUrl);
                    setSortOrder(itemData.sortOrder);
                    setStock(itemData.stock);
                    setManagesStock(itemData.managesStock);
                    setActive(itemData.active);
                    setCategoryId(itemData.categoryId);
                    if (itemData.rewards && rewardsData) {
                        const rewardsMap = new Map(rewardsData.map(r => [r.id, r]));
                        const enrichedPrices = itemData.rewards.map(price => {
                            const rewardDetails = rewardsMap.get(price.rewardId);
                            return {
                                ...price,
                                rewardName: rewardDetails ? rewardDetails.name : 'Unknown Reward',
                                iconUrl: rewardDetails ? rewardDetails.iconUrl : null
                            };
                        });
                        setPrices(enrichedPrices);
                    }
                }
            })
            .catch(err => setError(err.message || 'Failed to load page data'))
            .finally(() => setLoading(false));
    }, [id, isNew]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsSaving(true);
        setError(null);

        const itemData = {
            id: isNew ? null : id,
            name, description, imageUrl, sortOrder, stock, managesStock, active, categoryId,
            rewards: prices.map(p => ({rewardId: p.rewardId, quantity: p.quantity})),
        };

        try {
            const apiCall = isNew
                ? apiService.post('/api/admin/prizes/items', itemData)
                : apiService.put(`/api/admin/prizes/items/${id}`, itemData);

            await apiCall;
            setSnackbar({open: true, message: 'Prize item saved successfully!'});
            setTimeout(() => navigate('/prize-items'), 800);
        } catch (err) {
            setError(err.message || 'Failed to save prize item');
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) return <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}><CircularProgress/></Box>;

    return (
        <Box sx={{p: 2}} component="form" onSubmit={handleSubmit}>
            <Typography variant="h4" gutterBottom>
                {isNew ? 'Create Prize Item' : `Edit Item: ${name}`}
            </Typography>

            <Protected component={PrizeItemEditPage} action={requiredAction} mode="disable">
                <ProtectedFieldSet>
                    <Grid container spacing={3}>
                        {/* Left Column: Main Details  */}
                        <Grid size={{xs: 12, md: 6}}>
                            <Stack spacing={3}>
                                <Paper sx={{p: 2}}>
                                    <Typography variant="h6" gutterBottom>Configuration</Typography>
                                    <Stack spacing={2}>
                                        <TextField
                                            required
                                            fullWidth
                                            label="Item Name"
                                            value={name}
                                            onChange={e => setName(e.target.value)}
                                        />
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={4} // Start at 4 rows high
                                            maxRows={16} // Grow up to 16 rows, then scroll
                                            label="Description"
                                            value={description}
                                            onChange={e => setDescription(e.target.value)}
                                            helperText="Standard newlines (e.g., \n) and other special characters are supported."
                                        />
                                        <FormControl fullWidth required>
                                            <InputLabel>Category</InputLabel>
                                            <Select
                                                value={categoryId}
                                                label="Category"
                                                onChange={e => setCategoryId(e.target.value)}
                                            >
                                                {categories.map(cat => (
                                                    <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Stack>
                                </Paper>
                                <Paper sx={{p: 2}}>
                                    <Stack spacing={2}>
                                        <TextField
                                            required
                                            fullWidth
                                            type="number"
                                            label="Sort Order"
                                            value={sortOrder}
                                            onChange={e => setSortOrder(parseInt(e.target.value, 10))}
                                        />
                                        <TextField
                                            required
                                            fullWidth
                                            type="number"
                                            label="Stock"
                                            value={stock}
                                            onChange={e => setStock(parseInt(e.target.value, 10))}
                                        />
                                        <FormControlLabel
                                            control={<Switch
                                                checked={managesStock}
                                                onChange={e => setManagesStock(e.target.checked)}/>
                                            }
                                            label="Manages Stock"/>
                                        <FormControlLabel
                                            control={<Switch checked={active}
                                                             onChange={e => setActive(e.target.checked)}/>}
                                            label="Active"/>
                                    </Stack>
                                </Paper>
                            </Stack>
                        </Grid>

                        {/* Right Column: Image & Metadata */}
                        <Grid size={{xs: 12, md: 6}}>
                            <ImageUploader imageUrl={imageUrl} onImageUrlChange={setImageUrl} baseFileName={name}
                                           uploadPath="Prizes"/>
                        </Grid>
                    </Grid>
                </ProtectedFieldSet>
            </Protected>

            <Box sx={{mt: 3}}>
                <PrizePricesManager prices={prices} setPrices={setPrices} allRewards={allRewards}/>
            </Box>

            {error && <Alert severity="error" sx={{mt: 3}}>{error}</Alert>}

            <Stack direction="row" spacing={2} sx={{mt: 3}}>
                <Protected component={PrizeItemEditPage} action={requiredAction}>
                    <Button type="submit" variant="contained"
                            startIcon={isSaving ? <CircularProgress size={20}/> : <SaveIcon/>} disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save Item'}
                    </Button>
                </Protected>
                <Button component={RouterLink} to="/prize-items" variant="outlined" startIcon={<ArrowBackIcon/>}>
                    Back to List
                </Button>
            </Stack>

            <Snackbar open={snackbar.open} autoHideDuration={6000}
                      onClose={() => setSnackbar({open: false, message: ''})} message={snackbar.message}/>
        </Box>
    );
};

PrizeItemEditPage.abilities = {
    create: [ROLES.ADMINS],
    update: [ROLES.ADMINS],
    managePrices: [ROLES.ADMINS],
};

export default PrizeItemEditPage;